// Per-user Firefly visual/motion intensity — distinct from activity-dock
// assist level (peek aggressiveness). Composes with the platform-wide
// motion-speed multiplier (FF-0) via `computeFireflyMotionMultiplier`.
//
// Storage: localStorage key `bb-setting-firefly-visual-intensity`.
// Subscriber pattern mirrors `src/lib/activityDock/assistLevel.ts`.

import {
  clampFireflyMotionSpeedMultiplier,
  FIREFLY_DEFAULT_MOTION_SPEED_MULTIPLIER,
  FIREFLY_MOTION_SPEED_MAX,
  FIREFLY_MOTION_SPEED_MIN,
} from "./fireflyMotionSpeed";

export const FIREFLY_VISUAL_INTENSITY_STORAGE_KEY =
  "bb-setting-firefly-visual-intensity";

export const FIREFLY_VISUAL_INTENSITY_LEVELS = [
  "calm",
  "normal",
  "lively",
] as const;

export type FireflyVisualIntensity =
  (typeof FIREFLY_VISUAL_INTENSITY_LEVELS)[number];

export const FIREFLY_VISUAL_INTENSITY_DEFAULT: FireflyVisualIntensity = "normal";

/** Target bands on the FF-0 multiplier scale (0.5×–2.0×). */
export const FIREFLY_VISUAL_INTENSITY_MULTIPLIER_RANGES: Readonly<
  Record<
    FireflyVisualIntensity,
    { min: number; max: number; representative: number }
  >
> = {
  calm: { min: 0.5, max: 1.0, representative: 0.75 },
  normal: { min: 1.0, max: 1.5, representative: 1.25 },
  lively: { min: 1.5, max: 2.0, representative: 1.75 },
};

/**
 * Scales the platform multiplier into each intensity band.
 * Factors are tuned against the FF-0 default platform speed (1.5×):
 * calm → 0.75×, normal → 1.5×, lively → 2.0× (clamped).
 */
export const FIREFLY_VISUAL_INTENSITY_SPEED_FACTORS: Readonly<
  Record<FireflyVisualIntensity, number>
> = {
  calm:
    FIREFLY_VISUAL_INTENSITY_MULTIPLIER_RANGES.calm.representative /
    FIREFLY_DEFAULT_MOTION_SPEED_MULTIPLIER,
  normal: 1,
  lively: FIREFLY_MOTION_SPEED_MAX / FIREFLY_DEFAULT_MOTION_SPEED_MULTIPLIER,
};

export type FireflyVisualIntensityDescription = {
  id: FireflyVisualIntensity;
  label: string;
  blurb: string;
};

export const FIREFLY_VISUAL_INTENSITY_DESCRIPTIONS: readonly FireflyVisualIntensityDescription[] =
  [
    {
      id: "calm",
      label: "Calm",
      blurb: "Slower pulses and gentler drift — easier on the eyes.",
    },
    {
      id: "normal",
      label: "Normal (recommended)",
      blurb: "Balanced motion that matches the app’s tuned default speed.",
    },
    {
      id: "lively",
      label: "Lively",
      blurb: "Brisker animation and quicker highlight cycles.",
    },
  ];

export function parseFireflyVisualIntensity(
  value: unknown,
  fallback: FireflyVisualIntensity = FIREFLY_VISUAL_INTENSITY_DEFAULT,
): FireflyVisualIntensity {
  if (typeof value !== "string") return fallback;
  const match = FIREFLY_VISUAL_INTENSITY_LEVELS.find((level) => level === value);
  return match ?? fallback;
}

/** OS reduced-motion requests always resolve to calm intensity. */
export function resolveFireflyVisualIntensity(
  stored: FireflyVisualIntensity,
  prefersReducedMotion: boolean,
): FireflyVisualIntensity {
  return prefersReducedMotion ? "calm" : stored;
}

export function computeFireflyMotionMultiplier(
  platformMultiplier: number,
  intensity: FireflyVisualIntensity,
): number {
  const factor = FIREFLY_VISUAL_INTENSITY_SPEED_FACTORS[intensity];
  return clampFireflyMotionSpeedMultiplier(platformMultiplier * factor);
}

export function isFireflyVisualIntensityCapped(
  stored: FireflyVisualIntensity,
  prefersReducedMotion: boolean,
): boolean {
  return prefersReducedMotion && stored !== "calm";
}

type Listener = (next: FireflyVisualIntensity) => void;
const listeners = new Set<Listener>();

export function getFireflyVisualIntensity(): FireflyVisualIntensity {
  if (typeof localStorage === "undefined") {
    return FIREFLY_VISUAL_INTENSITY_DEFAULT;
  }
  try {
    return parseFireflyVisualIntensity(
      localStorage.getItem(FIREFLY_VISUAL_INTENSITY_STORAGE_KEY),
    );
  } catch {
    return FIREFLY_VISUAL_INTENSITY_DEFAULT;
  }
}

export function setFireflyVisualIntensity(next: FireflyVisualIntensity): void {
  if (typeof localStorage === "undefined") return;
  const value = parseFireflyVisualIntensity(next);
  try {
    localStorage.setItem(FIREFLY_VISUAL_INTENSITY_STORAGE_KEY, value);
  } catch {
    // Quota / private-mode — still notify in-tab subscribers.
  }
  for (const listener of listeners) listener(value);
}

export function subscribeFireflyVisualIntensity(listener: Listener): () => void {
  listeners.add(listener);
  let storageHandler: ((e: StorageEvent) => void) | null = null;
  if (typeof window !== "undefined") {
    storageHandler = (e) => {
      if (e.key !== FIREFLY_VISUAL_INTENSITY_STORAGE_KEY) return;
      listener(parseFireflyVisualIntensity(e.newValue));
    };
    window.addEventListener("storage", storageHandler);
  }
  return () => {
    listeners.delete(listener);
    if (storageHandler && typeof window !== "undefined") {
      window.removeEventListener("storage", storageHandler);
    }
  };
}

/** Test helper — verify composed multiplier lands in the intensity band. */
export function fireflyIntensityMultiplierInBand(
  multiplier: number,
  intensity: FireflyVisualIntensity,
): boolean {
  const { min, max } = FIREFLY_VISUAL_INTENSITY_MULTIPLIER_RANGES[intensity];
  return (
    multiplier >= Math.max(FIREFLY_MOTION_SPEED_MIN, min) - 1e-9 &&
    multiplier <= Math.min(FIREFLY_MOTION_SPEED_MAX, max) + 1e-9
  );
}
